import { useState, useEffect } from 'react';
import { heroContent, countryTicker } from '../data/mock';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, MessageCircle, Sparkles, GraduationCap, Globe, Award, Plane, BookOpen } from 'lucide-react';

export const HeroSection = ({ onBookDemo }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const handleMouse = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  const doubledTicker = [...countryTicker, ...countryTicker];

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-[#0A1628]">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Gradient orbs */}
        <div
          className="absolute top-1/4 -left-48 w-[500px] h-[500px] bg-[#D4A853]/8 rounded-full blur-[120px]"
          style={{ transform: `translate(${mousePos.x * 0.5}px, ${mousePos.y * 0.5}px)` }}
        />
        <div
          className="absolute bottom-1/4 -right-48 w-[500px] h-[500px] bg-[#243B6A]/40 rounded-full blur-[120px]"
          style={{ transform: `translate(${mousePos.x * -0.3}px, ${mousePos.y * -0.3}px)` }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#1B2A4A]/30 rounded-full blur-[150px]" />

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

        {/* Animated flying plane */}
        <div className="animate-fly-plane absolute top-1/3 left-0">
          <Plane className="w-8 h-8 text-[#D4A853]/15 rotate-[-30deg]" />
        </div>

        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#D4A853]/20"
            style={{
              width: `${4 + i * 2}px`,
              height: `${4 + i * 2}px`,
              top: `${15 + i * 15}%`,
              left: `${10 + i * 15}%`,
              animation: `particle-float ${6 + i * 2}s ease-in-out infinite ${i * 0.8}s`,
            }}
          />
        ))}
      </div>

      {/* Floating 3D Cards */}
      <div
        className="absolute top-36 right-24 hidden xl:block"
        style={{
          transform: `translate(${mousePos.x * 0.8}px, ${mousePos.y * 0.8}px)`,
          animation: 'float 6s ease-in-out infinite',
        }}
      >
        <div className="glass rounded-2xl p-5 flex items-center gap-3 shadow-xl">
          <div className="w-12 h-12 bg-[#D4A853]/15 rounded-xl flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-[#E8C87A]" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">3000+ Students</p>
            <p className="text-white/50 text-xs">Successfully Placed</p>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-48 right-36 hidden xl:block"
        style={{
          transform: `translate(${mousePos.x * -0.6}px, ${mousePos.y * -0.6}px)`,
          animation: 'float 8s ease-in-out infinite 1s',
        }}
      >
        <div className="glass rounded-2xl p-5 flex items-center gap-3 shadow-xl">
          <div className="w-12 h-12 bg-emerald-500/15 rounded-xl flex items-center justify-center">
            <Award className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">98% Visa</p>
            <p className="text-white/50 text-xs">Success Rate</p>
          </div>
        </div>
      </div>

      <div
        className="absolute top-52 left-16 hidden xl:block"
        style={{
          transform: `translate(${mousePos.x * 0.4}px, ${mousePos.y * 0.4}px)`,
          animation: 'float 5s ease-in-out infinite 0.5s',
        }}
      >
        <div className="glass rounded-2xl p-5 flex items-center gap-3 shadow-xl">
          <div className="w-12 h-12 bg-sky-400/15 rounded-xl flex items-center justify-center">
            <Globe className="w-6 h-6 text-sky-300" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">20+ Countries</p>
            <p className="text-white/50 text-xs">Global Network</p>
          </div>
        </div>
      </div>

      <div
        className="absolute bottom-56 left-32 hidden xl:block"
        style={{
          transform: `translate(${mousePos.x * 0.5}px, ${mousePos.y * -0.4}px)`,
          animation: 'float 7s ease-in-out infinite 2s',
        }}
      >
        <div className="glass rounded-2xl p-5 flex items-center gap-3 shadow-xl">
          <div className="w-12 h-12 bg-amber-400/15 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Band 7+ IELTS</p>
            <p className="text-white/50 text-xs">Proven Strategy</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 bg-[#D4A853]/10 border border-[#D4A853]/20 rounded-full px-5 py-2.5 mb-8 transition-[opacity,transform] duration-700 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Sparkles className="w-4 h-4 text-[#E8C87A]" />
            <span className="text-[#E8C87A] text-sm font-medium">Raipur's Most Trusted Education Consultant</span>
          </div>

          {/* Headline */}
          <h1
            className={`text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-7 transition-[opacity,transform] duration-700 delay-200 ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {heroContent.headline}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E8C87A] to-[#D4A853]">
              {heroContent.headlineAccent}
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className={`text-white/60 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl transition-[opacity,transform] duration-700 delay-[400ms] ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {heroContent.subheadline}
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 mb-12 transition-[opacity,transform] duration-700 delay-[600ms] ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Button
              onClick={onBookDemo}
              className="bg-[#D4A853] hover:bg-[#C9A84C] text-[#0A1628] font-bold px-8 py-6 text-lg rounded-full shadow-lg shadow-[#D4A853]/25 hover:shadow-[#D4A853]/40 transition-[background-color,box-shadow] duration-300 group"
            >
              {heroContent.ctaPrimary}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
            <a href={`https://wa.me/${heroContent.whatsappNumber}`} target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full transition-[background-color,border-color] duration-300"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {heroContent.ctaSecondary}
              </Button>
            </a>
          </div>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap gap-3 transition-[opacity,transform] duration-700 delay-[800ms] ${
              loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {heroContent.badges.map((badge) => (
              <Badge
                key={badge}
                variant="secondary"
                className="bg-white/5 border border-white/10 text-white/70 px-4 py-2 text-sm font-medium hover:bg-white/10 transition-colors duration-300"
              >
                {badge}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Country Ticker Marquee */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#D4A853]/5 border-y border-[#D4A853]/10 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-3">
          {doubledTicker.map((country, i) => (
            <span key={i} className="mx-6 text-white/40 text-sm font-medium flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4A853]/60" />
              {country}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
